// src/components/RegistrationForm.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utilities/api';
import { toast } from 'react-hot-toast';

const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const hasValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
const hasValidMobile = (value) => {
  const raw = String(value || '').trim();
  const digits = normalizePhone(raw);
  return /^[+\d\s().-]+$/.test(raw) && (digits.length === 10 || (digits.length === 11 && digits.startsWith('1')));
};
const identifierType = (value) => String(value || '').includes('@') ? 'email' : 'phone';

// Component receives callbacks and initial data from RegistrationWorkflow
function RegistrationForm({ onSuccess, setLoading, setError, initialData, onAccountRecovery }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
      ...initialData,
      user_name: initialData?.user_name || initialData?.email || initialData?.phone_mobile || '',
      prefix:  initialData?.prefix || '',
      firstname: initialData?.firstname || '',
      lastname:  initialData?.lastname || '',
      suffix:  initialData?.suffix || '',
      birthdate: initialData.birthdate || '',
      origin: window.location.origin || '',
      registration_step: "register",
    });

  const [isSubmitting, setIsSubmitting] = useState(false); // Local loading state for the form

  // Standard handler to update form state as user types
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default browser form submission
    setError(''); // Clear any previous errors shown in the parent
    setIsSubmitting(true); // Disable form inputs
    setLoading(true); // Show loading state in the parent workflow

    try {
      const enteredIdentifier = String(formData.user_name || '').trim();
      const loginMethod = identifierType(enteredIdentifier);
      const email = loginMethod === 'email' ? enteredIdentifier.toLowerCase() : '';
      const phoneMobile = loginMethod === 'phone' ? normalizePhone(enteredIdentifier) : '';
      const userName = loginMethod === 'phone' ? phoneMobile : email;

      if (!enteredIdentifier) {
        setError('Enter an email address or a mobile phone number.');
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      if (loginMethod === 'email' && !hasValidEmail(email)) {
        setError('Enter a valid email address.');
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      if (loginMethod === 'phone' && !hasValidMobile(phoneMobile)) {
        setError('Enter a valid 10-digit US mobile phone number.');
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      if (!userName) {
        setError('Choose an available email address or mobile phone number for sign in.');
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      if (!formData.birthdate || formData.birthdate > todayIso()) {
        setError('A valid birthdate is required.');
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      // Call the registration API function
      const registrationPayload = {
        ...formData,
        email,
        phone_mobile: phoneMobile,
        user_name: userName,
        identifier: userName,
        preferred_phone: formData.preferred_phone || 'MOBILE',
        login_method: loginMethod,
        preferred_contact: loginMethod === 'phone' ? 'TEXT' : 'EMAIL',
      };
      const response = await api.register(registrationPayload);
	      if (response.success) {
        // --- API Call Successful ---
	        // Prepare the data object to pass up to the workflow
        const successData = {
          userid: response?.userid,
          user_name: response?.user_name || registrationPayload.user_name,
          email: response?.email || registrationPayload.email, // Prefer response email if available
          phone_mobile: response?.phone_mobile || registrationPayload.phone_mobile,
          login_method: registrationPayload.login_method,
          prefix: response?.prefix || registrationPayload.prefix, // Prefer response prefix if available
          firstname: response?.firstname || registrationPayload.firstname, // Prefer response name if available
          lastname: response?.lastname || registrationPayload.lastname, // Prefer response name if available
          suffix: response?.suffix || registrationPayload.suffix, // Prefer response suffix if available
          birthdate: response?.birthdate || registrationPayload.birthdate || '',
          gender: response?.gender || registrationPayload.gender || '',
          height: response?.height || registrationPayload.height || '',
          weight: response?.weight || registrationPayload.weight || '',
          bmi: response?.bmi || registrationPayload.bmi || '',
          goal_weight: response?.goal_weight || response?.goalWeight || registrationPayload.goal_weight || registrationPayload.goal || '',
          capability: response?.capability || null,
          goalDate: response?.goal_date || response?.goalDate || '',
          startDate: response?.start_date || response?.startDate || '',
          requiresverification: response?.requiresverification || Boolean(response?.capability),
          verification_channel: response?.verification_channel || registrationPayload.preferred_contact,
          confirmed_email: response?.confirmed_email || '0',
          confirmed_phone: response?.confirmed_phone || '0',
          registration_step: 'register', // Pass registration step
        };

        // Pass the server-issued, purpose-scoped registration capability.
	        onSuccess(successData);

      } else {
        // --- API Call Failed (response.success is false) ---
	        if (response?.httpStatus === 409 && response?.action === 'account_recovery' && response?.recovery_token) {
          // Duplicate identity hard-reject (#203) — route into the
          // guided recovery flow instead of showing a generic form error.
          // Recovery state is owned by the PARENT (RegistrationWorkflow), not
          // this component: setLoading(true) above is the parent's setter and
          // swaps this form out for a Spinner while we're mid-await, so a
          // local setState here would land on an unmounted instance and be
          // dropped. Calling the parent's callback instead survives that.
          onAccountRecovery?.(response.recovery_token, response.error || 'An account already exists for this patient.');
          return;
        }
	        if(response?.redirect) {
          // If the API response indicates a redirect, handle it
          toast('This user already exists, redirecting to ' + response.redirect);
          navigate(`/${response.redirect}`);
          return; // Exit the function to prevent further execution
        }
        // Set the error message in the parent component
        setError(response.error || 'Registration failed. Please check your details.');
      }
    } catch {
      // --- Network or other unexpected error during API call ---
	      // Set a generic error message in the parent
      setError('An error occurred during registration. Please try again.');
      // State (formData) is NOT intentionally cleared here either.
    } finally {
      // This block runs regardless of success or failure
      setIsSubmitting(false); // Re-enable form inputs
      setLoading(false); // Hide loading state in the parent
    }
  };
	  // Render the form
  return (
    <form onSubmit={handleSubmit} className="space-y-2 md:space-y-4 ">
      <h3 className="text-lg font-semibold text-primary mb-4">Account Details</h3>

      <div>
        <label htmlFor="user_name" className="block text-sm font-medium text-neutral-dark mb-1">Username (email or mobile phone)</label>
        <input
          id="user_name"
          name="user_name"
          type="text"
          autoComplete="username"
          placeholder="Email address or mobile phone"
          value={formData.user_name}
          onChange={handleChange}
          className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
          required
          disabled={isSubmitting}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[5rem_minmax(0,1fr)_minmax(0,1fr)_5rem] gap-3 items-end">
        {/* Prefix Input */}
        <div>
          <label htmlFor="prefix" className="block text-sm font-medium text-neutral-dark mb-1">Prefix</label>
          <select
            id="prefix"
            name="prefix"
            type="text"
            placeholder="First Name"
            value={formData.prefix} // Bind value to state
            onChange={handleChange} // Update state on change
            className="w-full p-2 border bg-white rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={isSubmitting} // Disable when submitting
          >
            <option value=""></option>
            <option value="Dr">Dr.</option>
            <option value="Mr">Mr.</option>
            <option value="Mrs">Mrs.</option>
            <option value="Miss">Miss.</option>
            <option value="Ms">Ms.</option>
          </select>
        </div>

        {/* First Name Input */}
        <div>
          <label htmlFor="firstname" className="block text-sm font-medium text-neutral-dark mb-1">First Name</label>
          <input
            id="firstname"
            name="firstname"
            type="text"
            placeholder="First Name"
            value={formData.firstname} // Bind value to state
            onChange={handleChange} // Update state on change
            className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
            required // HTML5 validation
            disabled={isSubmitting} // Disable when submitting
          />
        </div>

        {/* Last Name Input */}
        <div>
          <label htmlFor="lastname" className="block text-sm font-medium text-neutral-dark mb-1">Last Name</label>
          <input
            id="lastname"
            name="lastname"
            type="text"
            placeholder="Last Name"
            value={formData.lastname} // Bind value to state
            onChange={handleChange} // Update state on change
            className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
            required // HTML5 validation
            disabled={isSubmitting} // Disable when submitting
          />
        </div>

        {/* Suffix Select */}
        <div>
          <label htmlFor="suffix" className="block text-sm font-medium text-neutral-dark mb-1">Suffix</label>
          <select
              id="suffix"
              name="suffix"
              value={formData.suffix || ''} // Bind value to state
              onChange={handleChange} // Update state on change
              className="w-full p-2 border rounded border-neutral bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting} // Disable when submitting
            >
              <option value=""></option>
              <option value="Sr">Sr</option>
              <option value="Jr">Jr</option>
              <option value="II">II</option>
              <option value="III">III</option>
              <option value="IV">IV</option>
              <option value="V">V</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="birthdate" className="block text-sm font-medium text-neutral-dark mb-1">Birthdate</label>
        <input
          id="birthdate"
          name="birthdate"
          type="date"
          placeholder="mm/dd/yyyy"
          max={todayIso()}
          value={formData.birthdate} // Bind value to state
          onChange={handleChange} // Update state on change
          className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
          required // HTML5 validation
          disabled={isSubmitting} // Disable when submitting
        />
      </div>


      {/* Submit Button */}
      <div className="pt-4">
          <button
              type="submit"
              className={`w-full bg-button text-white py-2 rounded-md hover:bg-button-light transition duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSubmitting} // Disable button when submitting
          >
              {isSubmitting ? 'Registering...' : 'Register & Continue'}
          </button>
      </div>
    </form>
  );
}

export default RegistrationForm;
